var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
// @ts-ignore
import * as callToActionUtils from 'conversional-utils/callToActions';
// @ts-ignore
import { getJourneySettings, persistCurrentJourneyOnSettings } from 'conversional-environment/settings';
import { addPreloaderIframe, getRootLauncher, parseQueryString, runLauncher } from './dom/utils';
import { addLauncherStyles } from './styles';
import { getJourneyConfig } from './api';
import { configureUrl } from './url';
import { deepMerge } from './functions';
import { initializeLauncher, removeFromDOM, removeIframesFromDOM } from './dom';
import { checkInlineWidgetAndRenderLoading, renderLoading } from './dom/loading';
import createIframes, { resetIframeCreation } from './createIframes';
import { waitForClient, waitForUserInteraction } from './wait';
import { processCTAClick } from './actions/actions';
import { captureConversionalSettingsChange, captureConversionalWidgetWrites, captureHostUrlChange } from './spa';
import { getHostWidget } from './config';
import { isTrackingEnabled } from 'conversional-analytics';
import { destroyBookingWidget, hideBookingWidget, isBookingWidgetActive, setBookingWidgetContext, shouldRenderBookingWidget, showBookingWidget, startBookingWidget } from './bookingWidget';
var _started = false;
// Expose public endpoint, needs to be available right from the start.
// Filled in key by key rather than assigned wholesale: a host page may have
// created the object itself before this bundle ran - to set the tracking
// switch below at the earliest possible moment, typically - and replacing it
// would drop what it wrote, while skipping it entirely (as an `if
// (!window.conversionalLauncher)` guard around the whole literal did) would
// leave that host without onReady.
if (!window.conversionalLauncher) {
    window.conversionalLauncher = {};
}
if (!window.conversionalLauncher.onReady) {
    window.conversionalLauncher.onReady = function () { return new Promise(function (resolve, reject) {
        waitForClient(function (data) {
            window.conversionalLauncher.isReady = true;
            resolve(data);
        }, reject);
    }); };
}
var _reloadInFlight = null;
window.conversionalLauncher = __assign(__assign({}, window.conversionalLauncher), { destroy: function () {
        destroyBookingWidget();
        removeFromDOM();
        removeIframesFromDOM();
        resetIframeCreation();
        _started = false;
    }, 
    /**
     * Opens the journey from host page code, e.g. a custom call-to-action. Data
     * for the widget is passed the usual way, by assigning
     * `window.conversionalWidget` before calling this (captured and forwarded
     * by captureConversionalWidgetWrites). `label` names the CTA in tracking.
     */
    open: function (label) {
        if (label === void 0) { label = 'HostApi'; }
        if (isBookingWidgetActive())
            return showBookingWidget();
        processCTAClick(label);
    }, 
    /**
     * New booking widget only: reveal / hide it. Inline it stays loaded while
     * hidden; start it hidden with `data-hidden` on its container.
     */
    show: showBookingWidget, hide: hideBookingWidget, 
    /**
     * New booking widget only: the branch / service type to book, e.g.
     * `setContext({ branchId })`. Updates an open widget in place.
     */
    setContext: setBookingWidgetContext, reload: function () {
        // If double initialization was a problem, this is the line
        // if (_reloadInFlight) return _reloadInFlight
        _reloadInFlight = Promise.resolve()
            .then(function () {
            destroyBookingWidget();
            removeFromDOM();
            removeIframesFromDOM();
            resetIframeCreation();
            _started = false;
            return bootstrap();
        })
            .finally(function () {
            _reloadInFlight = null;
        });
        return _reloadInFlight;
    }, 
    /**
     * Runtime tracking switch, for host pages that gate analytics behind their
     * own consent management. It takes effect immediately and for every tracker
     * we drive: TrackerProxy re-evaluates isTrackingEnabled() on every single
     * event, and events raised inside the iframes are relayed to this page
     * (MESSAGETYPES.TRACK_EVENT) before being tracked, so there is no second
     * gate to flip.
     *
     * The state lives on the launcher object as `trackingOverride` rather than
     * in module scope so it survives destroy()/reload() - both rebuild the DOM
     * and the iframes but never touch this object - and so a host can set it
     * before this bundle has even loaded.
     *
     * A call outranks every configured source (the journey config, the host
     * data objects, and storage consent): asking explicitly is the most
     * deliberate signal we get. resetTracking() drops the override and hands
     * the decision back to that configuration.
     *
     * disableTracking() stops events from being sent - analytics and Meta/Google
     * ad conversions alike. It does not retroactively delete cookies already
     * set; getTracker().updateCookiePreferences(false) is the tool for that.
     */
    enableTracking: function () {
        window.conversionalLauncher.trackingOverride = true;
    }, disableTracking: function () {
        window.conversionalLauncher.trackingOverride = false;
    }, resetTracking: function () {
        delete window.conversionalLauncher.trackingOverride;
    }, isTrackingEnabled: function () { return isTrackingEnabled(); }, inlineWidgetCheckerInterval: function () {
        if (window.conversionalLauncher.inlineCheckerInterval)
            return;
        window.conversionalLauncher.inlineCheckerInterval = setInterval(function () {
            if (!window.conversionalLauncher.inlineWidgetIds)
                return;
            window.conversionalLauncher.inlineWidgetIds.forEach(function (id) {
                var container = document.getElementById(id);
                if (!container)
                    return;
                if (container.getAttribute('data-conversional-initialized'))
                    return;
                window.conversionalLauncher.inlineCheckerInterval = null;
                clearInterval(window.conversionalLauncher.inlineCheckerInterval);
                window.conversionalLauncher.reload();
            });
        }, 1000);
    } });
var bootstrap = function () {
    return new Promise(function (resolve, reject) {
        var urlParams = parseQueryString();
        if (_started) {
            reject();
            return;
        }
        _started = true;
        runLauncher(function (settings) {
            if (getRootLauncher()) {
                reject();
                return;
            }
            var isLazyMode = function () { return getJourneySettings().bootstrap === 'lazy'; };
            window.conversionalLauncher.journeySettings = settings;
            window.conversionalLauncher.lazy = isLazyMode();
            // Mark the journey as active before its config is fetched. The settings
            // observer (spa.ts) compares data-journey with data-active-journey on every
            // body mutation, and the preloader iframe below already is one: an unset
            // value would read as a journey switch and start a second bootstrap
            // racing this one, repeating once a second until the config arrives.
            if (settings.journey)
                persistCurrentJourneyOnSettings(settings.journey);
            // Recognizing flash mode before fetching journey config
            if (!isLazyMode()) {
                // Just an iframe to fetch the main React bundle so it gets cached for the next loads
                addPreloaderIframe();
            }
            getJourneyConfig(settings).then(function (journeyConfig) {
                var _a, _b;
                if (shouldRenderBookingWidget(journeyConfig.configuration)) {
                    // The journey's whiteList / blackList of host URLs applies here as to the legacy widget
                    if (!configureUrl(journeyConfig.configuration)) {
                        reject();
                        return;
                    }
                    persistCurrentJourneyOnSettings(journeyConfig.configuration.id);
                    var start = function () { return startBookingWidget(settings, journeyConfig).then(resolve, reject); };
                    // delayForPageSpeed: nothing loads before the visitor's first interaction (startMainWidget does the same)
                    if (journeyConfig.configuration.delayForPageSpeed)
                        waitForUserInteraction(start);
                    else
                        start();
                    return;
                }
                // Deep merging passed and fetched values to make blackList and whiteList parameters overwritable
                var configuration = {
                    configuration: deepMerge(journeyConfig.configuration, 
                    // getHostWidget, not window.conversionalWidget: an integration
                    // snippet's iframe config (blackLists etc.) lives on
                    // window.conversionalIntegration, out of reach of host code that
                    // resets conversionalWidget.
                    ((_a = getHostWidget()) === null || _a === void 0 ? void 0 : _a.config) || {})
                }.configuration;
                if (!configureUrl(configuration)) {
                    reject();
                    return false;
                }
                addLauncherStyles();
                initializeLauncher(settings, journeyConfig);
                callToActionUtils.onCTAClick(processCTAClick);
                callToActionUtils.createStyleTagForCTAs();
                if (urlParams.dynamic || urlParams.openWidget) {
                    renderLoading(true);
                }
                checkInlineWidgetAndRenderLoading(configuration);
                persistCurrentJourneyOnSettings((_b = journeyConfig === null || journeyConfig === void 0 ? void 0 : journeyConfig.configuration) === null || _b === void 0 ? void 0 : _b.id);
                renderLoading(configuration.loading);
                startMainWidget(configuration).then(resolve);
            });
        });
    });
};
/**
 * If clients are concernec by the Google Pagespeed Ranking the actual loading of the tool can be delayed until the first user interaction
 * This is an opt-in feature because it may diminish user experience, especially on mobile devices and inline rendering where no user interaction may take place.
 */
function startMainWidget(configuration) {
    return new Promise(function (resolve, reject) {
        if (!configuration.delayForPageSpeed) {
            return createIframes().then(resolve);
        }
        return waitForUserInteraction(function () { return createIframes().then(resolve); });
    });
}
/**
 * Processes a CTA click by either executing it directly or persisting the click information to be processed once the client is ready.
 */
export default function () {
    return new Promise(function (resolve, reject) {
        captureConversionalSettingsChange();
        captureHostUrlChange();
        // Installed before bootstrap() does anything async, so a host write to
        // either host data object is caught no matter how early or late it lands.
        captureConversionalWidgetWrites();
        if (document.body) {
            return bootstrap().then(resolve);
        }
        window.addEventListener('DOMContentLoaded', function () {
            bootstrap().then(resolve);
        });
    });
}
